/* Bettr Sauna — Cross-tab state coordination
 * --------------------------------------------------------------------
 * Phase 17 — two browser APIs working together:
 *
 *   BroadcastChannel — when one tab mutates the cart, every other
 *     open tab gets notified. Keeps the cart count + totals in sync
 *     across multiple shop tabs.
 *
 *   Web Locks — navigator.locks.request serializes cart mutations
 *     across tabs. Prevents the "user clicks + in tab A and + in tab
 *     B simultaneously, both read qty=1, both write qty=2, we lose a
 *     unit" race. The lock is held for the duration of each mutation
 *     and released automatically when the callback resolves.
 *
 * Public API (attached to window.BettrCrossTab):
 *   .broadcast(type, detail)       — send to other tabs
 *   .onMessage(type, handler)      — subscribe, returns unsubscriber
 *   .withLock(name, fn)            — run fn under a named Web Lock
 *
 * Removed 2026-05-23:
 *   - IndexedDB cart mirror (cacheCart/readCart). The write path ran
 *     on every cart:updated; the read path had zero consumers — the
 *     cart drawer always rendered from server-side Liquid on first
 *     paint. Dead code path.
 *   - Opportunistic /cart.js fetch inside the cart:updated listener.
 *     The cart drawer now passes the cart in ev.detail.cart, so this
 *     redundant fetch is no longer needed.
 *
 * Note: cross-tab broadcasts intentionally carry no cart payload —
 * BroadcastChannel structured-clone limits + the receiving tab needing
 * to confirm its own state make a re-dispatch the simpler contract.
 * The same-tab cart:updated event DOES carry the cart object in
 * ev.detail.cart so local listeners (validators) can skip the
 * redundant /cart.js fetch.
 * -------------------------------------------------------------------- */

(function () {
  'use strict';

  const CHANNEL = 'bettr-cart';

  // ------------------------------------------------------------------
  // BroadcastChannel setup with fallback to storage event.
  // ------------------------------------------------------------------
  let channel = null;
  const listeners = new Map();

  if ('BroadcastChannel' in self) {
    try {
      channel = new BroadcastChannel(CHANNEL);
      channel.addEventListener('message', (ev) => dispatchIncoming(ev.data));
    } catch (_) { channel = null; }
  }

  // Fallback path for private-mode Safari / old browsers: localStorage
  // writes fire a storage event in other tabs (not the writing tab).
  if (!channel) {
    window.addEventListener('storage', (ev) => {
      if (ev.key !== CHANNEL || !ev.newValue) return;
      try { dispatchIncoming(JSON.parse(ev.newValue)); } catch (_) {}
    });
  }

  function dispatchIncoming(msg) {
    if (!msg || !msg.type) return;
    const handlers = listeners.get(msg.type);
    if (!handlers) return;
    handlers.forEach((h) => { try { h(msg.detail); } catch (_) {} });
  }

  function broadcast(type, detail) {
    const msg = { type, detail, from: Date.now() };
    if (channel) {
      try { channel.postMessage(msg); return; } catch (_) {}
    }
    try {
      localStorage.setItem(CHANNEL, JSON.stringify(msg));
      // Clear so next write with same payload still fires storage event.
      setTimeout(() => {
        try { localStorage.removeItem(CHANNEL); } catch (_) {}
      }, 50);
    } catch (_) {}
  }

  function onMessage(type, handler) {
    if (!listeners.has(type)) listeners.set(type, new Set());
    listeners.get(type).add(handler);
    return () => listeners.get(type).delete(handler);
  }

  // ------------------------------------------------------------------
  // Web Locks.
  // ------------------------------------------------------------------
  async function withLock(name, fn) {
    if (!('locks' in navigator) || typeof navigator.locks.request !== 'function') {
      return await fn();
    }
    return navigator.locks.request(name, { mode: 'exclusive' }, async () => await fn());
  }

  // ------------------------------------------------------------------
  // Wire up: when this tab dispatches cart:updated, echo to other tabs.
  // When another tab broadcasts, dispatch locally so listeners react.
  // The cart object (if any) in ev.detail is NOT forwarded across tabs —
  // each tab manages its own UI state and validators re-fetch as needed.
  // ------------------------------------------------------------------
  document.addEventListener('cart:updated', function (ev) {
    // Don't re-broadcast if the event itself came from a cross-tab relay.
    if (ev && ev.detail && ev.detail.__fromBroadcast) return;
    broadcast('cart:updated', {});
  });

  document.addEventListener('cart:cleaned', function (ev) {
    if (ev && ev.detail && ev.detail.__fromBroadcast) return;
    broadcast('cart:cleaned', {});
  });

  onMessage('cart:updated', function () {
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: { __fromBroadcast: true }
    }));
  });
  onMessage('cart:cleaned', function () {
    document.dispatchEvent(new CustomEvent('cart:cleaned', {
      detail: { __fromBroadcast: true }
    }));
  });

  window.BettrCrossTab = { broadcast, onMessage, withLock };
})();
